import React from 'react';
import { Box, CircularProgress } from '@mui/material';

const HypeSpinner = ({ 
  size = 'md', 
  variant = 'circle',
  className = '',
  sx = {}
}) => {
  const sizeValues = {
    sm: 16,
    md: 32,
    lg: 48
  };

  const dotSizes = {
    sm: 4,
    md: 8,
    lg: 12
  };

  const getThemeColors = (theme) => {
    const isDark = theme?.palette?.mode === 'dark';
    return {
      primary: isDark ? '#ffc107' : '#1976d2',
      secondary: isDark ? '#ffeb3b' : '#42a5f5',
      tertiary: isDark ? '#ff8f00' : '#1565c0',
      accent: isDark ? '#ffc107' : '#1976d2'
    };
  };

  if (variant === 'dots') {
    return (
      <Box 
        className={className}
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 0.5,
          ...sx 
        }}
      >
        {[0, 1, 2].map((index) => (
          <Box
            key={index}
            sx={{
              width: dotSizes[size],
              height: dotSizes[size],
              borderRadius: '50%',
              bgcolor: (theme) => {
                const colors = getThemeColors(theme);
                return index === 0 ? colors.primary : index === 1 ? colors.secondary : colors.tertiary;
              },
              animation: 'bounce 1.4s infinite ease-in-out',
              animationDelay: `${index * 0.16}s`,
              '@keyframes bounce': {
                '0%, 80%, 100%': { 
                  transform: 'scale(0)',
                  opacity: 0.5
                },
                '40%': { 
                  transform: 'scale(1)',
                  opacity: 1
                }
              }
            }}
          />
        ))}
      </Box>
    );
  }

  if (variant === 'pulse') {
    return (
      <Box 
        className={className}
        sx={{
          width: sizeValues[size],
          height: sizeValues[size],
          ...sx
        }}
      >
        <Box
          sx={{
            width: '100%',
            height: '100%',
            borderRadius: '50%',
            background: (theme) => {
              const colors = getThemeColors(theme);
              return `linear-gradient(135deg, ${colors.primary} 0%, ${colors.tertiary} 100%)`;
            },
            animation: 'pulse 2s infinite',
            '@keyframes pulse': {
              '0%': { 
                transform: 'scale(0.95)',
                boxShadow: (theme) => `0 0 0 0 ${getThemeColors(theme).primary}40`
              },
              '70%': { 
                transform: 'scale(1)',
                boxShadow: (theme) => `0 0 0 10px ${getThemeColors(theme).primary}00`
              },
              '100%': { 
                transform: 'scale(0.95)',
                boxShadow: (theme) => `0 0 0 0 ${getThemeColors(theme).primary}00`
              }
            }
          }}
        />
      </Box>
    );
  }

  if (variant === 'entertainment') {
    return (
      <Box 
        className={className}
        sx={{
          position: 'relative',
          width: sizeValues[size],
          height: sizeValues[size],
          ...sx
        }}
      >
        {/* Outer spinning ring */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            borderRadius: '50%',
            border: '2px solid transparent',
            background: (theme) => {
              const colors = getThemeColors(theme);
              return `linear-gradient(45deg, ${colors.primary}, ${colors.secondary}, ${colors.tertiary}) border-box`;
            },
            mask: 'linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)',
            maskComposite: 'xor',
            WebkitMask: 'linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)',
            WebkitMaskComposite: 'xor',
            animation: 'spin 2s linear infinite',
            '@keyframes spin': {
              '0%': { transform: 'rotate(0deg)' },
              '100%': { transform: 'rotate(360deg)' }
            }
          }}
        >
          <Box
            sx={{
              width: '100%',
              height: '100%',
              borderRadius: '50%',
              bgcolor: 'background.default'
            }}
          />
        </Box>
        
        {/* Inner content */}
        <Box
          sx={{
            position: 'absolute',
            inset: '4px',
            borderRadius: '50%',
            backdropFilter: 'blur(4px)',
            bgcolor: (theme) => theme.palette.mode === 'dark' 
              ? 'rgba(0,0,0,0.5)' 
              : 'rgba(255,255,255,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Box
            sx={{
              width: 0,
              height: 0,
              borderLeft: (theme) => `6px solid ${getThemeColors(theme).accent}`,
              borderTop: '4px solid transparent',
              borderBottom: '4px solid transparent',
              animation: 'pulse 1.5s infinite',
              ml: '2px'
            }}
          />
        </Box>
        
        {/* Floating dots */}
        <Box
          sx={{
            position: 'absolute',
            top: -2,
            left: -2,
            width: 4,
            height: 4,
            bgcolor: (theme) => getThemeColors(theme).secondary,
            borderRadius: '50%',
            animation: 'ping 2s infinite',
            '@keyframes ping': {
              '75%, 100%': {
                transform: 'scale(2)',
                opacity: 0
              }
            }
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            top: -2,
            right: -2,
            width: 4,
            height: 4,
            bgcolor: (theme) => getThemeColors(theme).tertiary,
            borderRadius: '50%',
            animation: 'ping 2s infinite 0.5s'
          }}
        />
      </Box>
    );
  }

  // Default circle variant
  return (
    <CircularProgress
      className={className}
      size={sizeValues[size]}
      thickness={4}
      sx={{
        color: (theme) => getThemeColors(theme).primary,
        '& .MuiCircularProgress-circle': {
          strokeLinecap: 'round',
        },
        ...sx
      }}
    />
  );
};

export default HypeSpinner;